import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Users,
  Container,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Send,
  BarChart3,
  Database,
  Download,
  Upload,
  HardDrive,
  FileJson,
  Trash2,
  FolderOpen,
  Truck,
  FileText,
  Train,
  Calculator,
  Fuel,
  Settings,
  Globe,
  TrendingUp,
  Zap,
} from "lucide-react";
import { DataImportButton } from "../components/DataImportModal";
import { API_URL, API_BASE_URL } from "../config/api";


export default function AdminPage() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [updateForm, setUpdateForm] = useState({
    status: "",
    location: "",
    vessel_name: "",
    eta: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { getAuthHeaders } = useAuth();
  const token = localStorage.getItem("token");
  
  // Database management state
  const [dbStats, setDbStats] = useState(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [reactSends, setReactSends] = useState(null);
  const [reactLoading, setReactLoading] = useState(false);
  const [repliedList, setRepliedList] = useState([]);
  const [repliedLoading, setRepliedLoading] = useState(false);
  const [markReplyForm, setMarkReplyForm] = useState({
    customer_email: "",
    cooldown_months: 3,
    reply_subject: "",
    reply_preview: "",
  });
  const [markReplyOpen, setMarkReplyOpen] = useState(false);
  const [markReplySubmitting, setMarkReplySubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [mergeMode, setMergeMode] = useState("skip");
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Route pricing configuration state
  const [pricingConfig, setPricingConfig] = useState({
    ftl_base_rate_per_km: 1.35,
    ltl_base_rate_per_km: 1.85,
    fuel_surcharge_percent: 25,
    handling_cost_per_shipment: 35,
    margin_percent: 15,
    min_charge_eur: 150,
    diesel_price_per_liter: 1.65,
    kg_per_ldm: 1850,
    max_truck_ldm: 13.6,
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);
  
  // Fuel prices state
  const [fuelPrices, setFuelPrices] = useState(null);
  const [fuelLoading, setFuelLoading] = useState(false);
  const [fuelScraping, setFuelScraping] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, usersRes, analyticsRes, pendingRegsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/pending-requests`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/tracking-analytics`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/auth/pending-registrations`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);
      setPendingRequests(requestsRes.data);
      setUsers(usersRes.data);
      setAnalytics(analyticsRes.data);
      setPendingRegistrations(pendingRegsRes.data);
    } catch (error) {
      toast.error("Failed to fetch admin data");
    } finally {
      setLoading(false);
    }
  };

  const approveRegistration = async (userId) => {
    try {
      await axios.post(`${API_URL}/auth/approve-registration/${userId}`, {}, { headers: getAuthHeaders() });
      toast.success("Registration approved!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const rejectRegistration = async (userId) => {
    const reason = prompt("Rejection reason (optional):");
    try {
      await axios.post(`${API_URL}/auth/reject-registration/${userId}?reason=${encodeURIComponent(reason || "")}`, {}, { headers: getAuthHeaders() });
      toast.success("Registration rejected");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    }
  };

  // Route pricing configuration functions
  const fetchPricingConfig = async () => {
    setPricingLoading(true);
    try {
      const response = await axios.get(`${API_URL}/route/pricing-config`, { headers: getAuthHeaders() });
      setPricingConfig(response.data);
    } catch (error) {
      console.log("Could not fetch pricing config, using defaults");
    } finally {
      setPricingLoading(false);
    }
  };

  const savePricingConfig = async () => {
    setPricingSaving(true);
    try {
      const response = await axios.put(`${API_URL}/route/pricing-config`, pricingConfig, { headers: getAuthHeaders() });
      setPricingConfig(response.data);
      toast.success("Pricing configuration saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save pricing config");
    } finally {
      setPricingSaving(false);
    }
  };

  // Fuel prices functions
  const fetchFuelPrices = async () => {
    setFuelLoading(true);
    try {
      const response = await axios.get(`${API_URL}/fuel-prices`, { headers: getAuthHeaders() });
      setFuelPrices(response.data);
    } catch (error) {
      console.log("Could not fetch fuel prices");
    } finally {
      setFuelLoading(false);
    }
  };

  const scrapeFuelPrices = async () => {
    setFuelScraping(true);
    try {
      const response = await axios.post(`${API_URL}/fuel-prices/scrape`, {}, { headers: getAuthHeaders() });
      setFuelPrices(response.data);
      toast.success(`Fuel prices updated from ${response.data.source}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scrape fuel prices");
    } finally {
      setFuelScraping(false);
    }
  };

  const applyFuelPrice = async (countryCode) => {
    try {
      const response = await axios.post(`${API_URL}/fuel-prices/apply-to-config?country_code=${countryCode}`, {}, { headers: getAuthHeaders() });
      setPricingConfig(response.data.updated_config);
      toast.success(`Diesel price updated to €${response.data.diesel_price_eur} from ${response.data.country}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply fuel price");
    }
  };

  // Database management functions
  const fetchDbStats = async () => {
    setDbLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/database/stats`, { headers: getAuthHeaders() });
      setDbStats(response.data);
    } catch (error) {
      toast.error("Failed to fetch database stats");
    } finally {
      setDbLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/admin/database/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `myeuropacific_backup_${new Date().toISOString().slice(0, 10)}.json`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Database exported successfully! Save this file safely.");
    } catch (error) {
      toast.error("Failed to export database");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await axios.post(
        `${API_URL}/admin/database/import?merge_mode=${mergeMode}`,
        formData,
        { 
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setImportResult(response.data);
      toast.success(`Import complete! ${response.data.summary?.total_imported || 0} documents imported`);
      fetchDbStats(); // Refresh stats
      fetchData(); // Refresh all data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import database");
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDbStats();
    fetchPricingConfig();
    fetchFuelPrices();
    fetchReactSends();
    fetchRepliedList();
  }, []);

  const fetchReactSends = async () => {
    setReactLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/reactivation-sends`, { headers: getAuthHeaders() });
      setReactSends(res.data);
    } catch (e) {
      // silently ignore — stat panel is informational
    } finally {
      setReactLoading(false);
    }
  };

  const fetchRepliedList = async () => {
    setRepliedLoading(true);
    try {
      const res = await axios.get(`${API_URL}/reactivation/customers/replied?limit=50`, { headers: getAuthHeaders() });
      setRepliedList(res.data?.items || []);
    } catch (e) {
      // silently ignore
    } finally {
      setRepliedLoading(false);
    }
  };

  const handleMarkReplied = async () => {
    if (!markReplyForm.customer_email || !markReplyForm.customer_email.includes("@")) {
      toast.error("Please enter a valid customer email");
      return;
    }
    setMarkReplySubmitting(true);
    try {
      await axios.post(
        `${API_URL}/reactivation/customers/mark-replied`,
        markReplyForm,
        { headers: getAuthHeaders() },
      );
      toast.success(`Marked ${markReplyForm.customer_email} as replied — re-arm in ${markReplyForm.cooldown_months} months`);
      setMarkReplyOpen(false);
      setMarkReplyForm({ customer_email: "", cooldown_months: 3, reply_subject: "", reply_preview: "" });
      fetchRepliedList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark replied");
    } finally {
      setMarkReplySubmitting(false);
    }
  };

  const handleRearmNow = async (customer_email) => {
    if (!window.confirm(`Re-arm ${customer_email} now? They will become eligible for a new Stage 1 check-in on the next batch.`)) {
      return;
    }
    try {
      await axios.post(
        `${API_URL}/reactivation/customers/rearm`,
        { customer_email },
        { headers: getAuthHeaders() },
      );
      toast.success(`Re-armed ${customer_email}`);
      fetchRepliedList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to re-arm");
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest || !updateForm.status || !updateForm.location) {
      toast.error("Please fill in status and location");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/admin/update-status`,
        {
          request_id: selectedRequest.id,
          container_number: selectedRequest.container_number,
          ...updateForm,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Status updated and customer notified!");
      setSelectedRequest(null);
      setUpdateForm({ status: "", location: "", vessel_name: "", eta: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex items-center justify-between"
          >
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-2">
                ADMIN <span className="text-primary">PANEL</span>
              </h1>
              <p className="text-muted-foreground">
                Manage pending tracking requests and view analytics
              </p>
            </div>
            <Button
              variant="outline"
              onClick={fetchData}
              className="border-border hover:border-primary/50"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </motion.div>

          {/* Analytics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          >
            <div className="card-surface p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <span className="text-muted-foreground text-sm">Total Queries</span>
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {analytics?.total_queries || 0}
              </p>
            </div>

            <div className="card-surface p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded bg-accent/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-accent" strokeWidth={1.5} />
                </div>
                <span className="text-muted-foreground text-sm">Active Users</span>
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {analytics?.unique_users || 0}
              </p>
            </div>

            <div className="card-surface p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded bg-warning/10 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-warning" strokeWidth={1.5} />
                </div>
                <span className="text-muted-foreground text-sm">Pending Requests</span>
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {analytics?.pending_requests || 0}
              </p>
            </div>

            <div className="card-surface p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded bg-success/10 flex items-center justify-center">
                  <Container className="h-5 w-5 text-success" strokeWidth={1.5} />
                </div>
                <span className="text-muted-foreground text-sm">Total Users</span>
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {users.length}
              </p>
            </div>
          </motion.div>

          {/* Pending Requests */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-surface p-6 mb-8"
          >
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">
              Pending Manual Tracking Requests
            </h2>

            {pendingRequests.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-success/50 mx-auto mb-3" />
                <p className="text-muted-foreground">No pending requests</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Container</TableHead>
                      <TableHead className="text-muted-foreground">Carrier</TableHead>
                      <TableHead className="text-muted-foreground">Customer</TableHead>
                      <TableHead className="text-muted-foreground">Requested At</TableHead>
                      <TableHead className="text-muted-foreground">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingRequests.map((request) => (
                      <TableRow key={request.id} className="border-border">
                        <TableCell className="font-code text-foreground">
                          {request.container_number}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {request.carrier}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-foreground">{request.user_name}</p>
                            <p className="text-xs text-muted-foreground">{request.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(request.requested_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                className="btn-industrial text-xs"
                                onClick={() => setSelectedRequest(request)}
                                data-testid={`update-status-btn-${request.id}`}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Update
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-card border-border">
                              <DialogHeader>
                                <DialogTitle className="font-heading text-foreground">
                                  Update Container Status
                                </DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                <div className="p-3 bg-background rounded border border-border/50">
                                  <p className="font-code text-lg text-foreground">
                                    {selectedRequest?.container_number}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    Customer: {selectedRequest?.user_name}
                                  </p>
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-foreground">Status *</Label>
                                  <Input
                                    placeholder="e.g., In Transit, Arrived at Port"
                                    value={updateForm.status}
                                    onChange={(e) =>
                                      setUpdateForm((prev) => ({ ...prev, status: e.target.value }))
                                    }
                                    className="input-industrial"
                                    data-testid="status-input"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-foreground">Location *</Label>
                                  <Input
                                    placeholder="e.g., Rotterdam, Netherlands"
                                    value={updateForm.location}
                                    onChange={(e) =>
                                      setUpdateForm((prev) => ({ ...prev, location: e.target.value }))
                                    }
                                    className="input-industrial"
                                    data-testid="location-input"
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label className="text-foreground">Vessel Name</Label>
                                    <Input
                                      placeholder="e.g., MSC OSCAR"
                                      value={updateForm.vessel_name}
                                      onChange={(e) =>
                                        setUpdateForm((prev) => ({ ...prev, vessel_name: e.target.value }))
                                      }
                                      className="input-industrial"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-foreground">ETA</Label>
                                    <Input
                                      type="date"
                                      value={updateForm.eta}
                                      onChange={(e) =>
                                        setUpdateForm((prev) => ({ ...prev, eta: e.target.value }))
                                      }
                                      className="input-industrial"
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-foreground">Notes</Label>
                                  <Textarea
                                    placeholder="Additional notes for the customer..."
                                    value={updateForm.notes}
                                    onChange={(e) =>
                                      setUpdateForm((prev) => ({ ...prev, notes: e.target.value }))
                                    }
                                    className="bg-background border-border"
                                  />
                                </div>

                                <Button
                                  onClick={handleUpdateStatus}
                                  className="btn-industrial w-full"
                                  disabled={submitting}
                                  data-testid="submit-status-btn"
                                >
                                  {submitting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Send className="h-4 w-4 mr-2" />
                                      Update & Notify Customer
                                    </>
                                  )}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </motion.div>

          {/* Pending Registrations */}
          {pendingRegistrations.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="card-surface p-6 mb-8 border-2 border-warning/30"
            >
              <h2 className="font-heading text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-warning" />
                Pending Customer Registrations ({pendingRegistrations.length})
              </h2>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Name</TableHead>
                      <TableHead className="text-muted-foreground">Email</TableHead>
                      <TableHead className="text-muted-foreground">Company</TableHead>
                      <TableHead className="text-muted-foreground">Requested</TableHead>
                      <TableHead className="text-muted-foreground">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingRegistrations.map((reg) => (
                      <TableRow key={reg.id} className="border-border">
                        <TableCell className="text-foreground font-medium">
                          {reg.full_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{reg.email}</TableCell>
                        <TableCell className="text-foreground">{reg.company_name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(reg.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-success hover:bg-success/80 text-white"
                              onClick={() => approveRegistration(reg.id)}
                              data-testid={`approve-btn-${reg.id}`}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => rejectRegistration(reg.id)}
                              data-testid={`reject-btn-${reg.id}`}
                            >
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </motion.div>
          )}

          {/* Users List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-surface p-6 mb-8"
          >
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">
              Registered Users
            </h2>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Name</TableHead>
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground">Company</TableHead>
                    <TableHead className="text-muted-foreground">Joined</TableHead>
                    <TableHead className="text-muted-foreground">Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className="border-border">
                      <TableCell className="text-foreground font-medium">
                        {user.full_name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell className="text-muted-foreground">{user.company_name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`status-badge ${
                            user.is_admin ? "status-warning" : "status-info"
                          }`}
                        >
                          {user.is_admin ? "Admin" : "User"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </motion.div>

          {/* Database Management Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card-surface p-6 border-2 border-primary/30"
            data-testid="database-management-section"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center">
                <Database className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground">
                  Database Management
                </h2>
                <p className="text-sm text-muted-foreground">
                  Export data for backup or import to restore/migrate
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchDbStats}
                disabled={dbLoading}
                className="ml-auto"
              >
                <RefreshCw className={`h-4 w-4 ${dbLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Reactivation sends — last 24h */}
            {reactSends && (
              <div className={`mb-4 p-4 rounded-lg border ${
                reactSends.level === 'critical' ? 'bg-red-50 border-red-300 dark:bg-red-950/30' :
                reactSends.level === 'warning' ? 'bg-amber-50 border-amber-300 dark:bg-amber-950/30' :
                'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20'
              }`} data-testid="reactivation-sends-panel">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Send className={`h-4 w-4 ${
                        reactSends.level === 'critical' ? 'text-red-600' :
                        reactSends.level === 'warning' ? 'text-amber-600' :
                        'text-emerald-600'
                      }`} />
                      Reactivation sends — last 24h
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Spam / runaway-loop monitor. Alert threshold: {reactSends.daily_cap}/day.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium uppercase ${
                      reactSends.level === 'critical' ? 'bg-red-600 text-white' :
                      reactSends.level === 'warning' ? 'bg-amber-500 text-white' :
                      'bg-emerald-600 text-white'
                    }`} data-testid="reactivation-level-badge">{reactSends.level}</span>
                    <Button variant="ghost" size="sm" onClick={fetchReactSends} disabled={reactLoading} data-testid="reactivation-refresh-btn">
                      <RefreshCw className={`h-3.5 w-3.5 ${reactLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div className="bg-background/70 rounded px-3 py-2 border border-border/40">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Sends 24h</p>
                    <p className={`text-2xl font-bold ${
                      reactSends.level === 'critical' ? 'text-red-700' :
                      reactSends.level === 'warning' ? 'text-amber-700' :
                      'text-emerald-700'
                    }`} data-testid="reactivation-total-sends">
                      {reactSends.total_sends_24h}
                      <span className="text-sm font-normal text-muted-foreground"> / {reactSends.daily_cap}</span>
                    </p>
                  </div>
                  <div className="bg-background/70 rounded px-3 py-2 border border-border/40">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Unique recipients</p>
                    <p className="text-2xl font-bold text-foreground">{reactSends.unique_recipients_24h}</p>
                  </div>
                  <div className="bg-background/70 rounded px-3 py-2 border border-border/40">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Dup recipients</p>
                    <p className={`text-2xl font-bold ${(reactSends.duplicates || []).length ? 'text-red-700' : 'text-emerald-700'}`} data-testid="reactivation-dup-count">
                      {(reactSends.duplicates || []).length}
                    </p>
                  </div>
                </div>
                {(reactSends.duplicates || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border/40">
                    <p className="text-xs font-semibold text-foreground mb-2">Recipients hit 2+ times (potential spam)</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {reactSends.duplicates.slice(0, 20).map(d => (
                        <div key={d.customer_email} className="flex justify-between text-xs bg-background/50 rounded px-2 py-1">
                          <span className="font-mono truncate">{d.customer_email}</span>
                          <span className="font-semibold text-red-700 ml-2">×{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reactivation — Replied customers (paused, awaiting re-arm) */}
            <div className="mb-4 p-4 rounded-lg border bg-sky-50/60 border-sky-200 dark:bg-sky-950/20 dark:border-sky-900" data-testid="reactivation-replied-panel">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Send className="h-4 w-4 text-sky-700" />
                    Reactivation — Replies & paused customers
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Customers who replied. Voucher / cold-alert sends are halted until re-arm. Auto-rearm runs daily.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Dialog open={markReplyOpen} onOpenChange={setMarkReplyOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="default" data-testid="mark-replied-open-btn">
                        Mark customer replied
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Mark customer replied</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">Customer email *</Label>
                          <Input
                            value={markReplyForm.customer_email}
                            onChange={(e) => setMarkReplyForm({ ...markReplyForm, customer_email: e.target.value.trim() })}
                            placeholder="customer@example.com"
                            data-testid="mark-replied-email-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Cooldown (months) — next "reminder #1"</Label>
                          <Input
                            type="number" min="1" max="24"
                            value={markReplyForm.cooldown_months}
                            onChange={(e) => setMarkReplyForm({ ...markReplyForm, cooldown_months: Number(e.target.value) || 3 })}
                            data-testid="mark-replied-cooldown-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Reply subject (optional)</Label>
                          <Input
                            value={markReplyForm.reply_subject}
                            onChange={(e) => setMarkReplyForm({ ...markReplyForm, reply_subject: e.target.value })}
                            placeholder="Re: Checking if you still need transport support"
                            data-testid="mark-replied-subject-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Reply text snippet (optional, first 500 chars saved)</Label>
                          <Textarea
                            rows={4}
                            value={markReplyForm.reply_preview}
                            onChange={(e) => setMarkReplyForm({ ...markReplyForm, reply_preview: e.target.value })}
                            placeholder="Paste a few lines from the reply for context…"
                            data-testid="mark-replied-preview-input"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                          <Button variant="outline" size="sm" onClick={() => setMarkReplyOpen(false)} data-testid="mark-replied-cancel-btn">Cancel</Button>
                          <Button size="sm" onClick={handleMarkReplied} disabled={markReplySubmitting} data-testid="mark-replied-submit-btn">
                            {markReplySubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark replied & pause"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button variant="ghost" size="sm" onClick={fetchRepliedList} disabled={repliedLoading} data-testid="reactivation-replied-refresh-btn">
                    <RefreshCw className={`h-3.5 w-3.5 ${repliedLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {repliedList.length === 0 ? (
                <p className="text-xs text-muted-foreground italic" data-testid="replied-empty-state">
                  No customers currently in "Replied — paused" state.
                </p>
              ) : (
                <div className="max-h-72 overflow-y-auto space-y-2" data-testid="replied-list">
                  {repliedList.map((r) => {
                    const rearmDate = r.rearm_at ? new Date(r.rearm_at) : null;
                    const daysUntil = rearmDate ? Math.ceil((rearmDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                    return (
                      <div key={r.customer_email} className="bg-background/70 rounded border border-border/40 px-3 py-2" data-testid={`replied-row-${r.customer_email}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium font-mono truncate">{r.customer_email}</span>
                              <span className="text-[10px] uppercase tracking-wide bg-sky-600 text-white px-1.5 py-0.5 rounded">
                                Replied — Pause
                              </span>
                              {r.reply_detected_by === "auto_inbound" && (
                                <span className="text-[10px] uppercase tracking-wide bg-emerald-600 text-white px-1.5 py-0.5 rounded">auto</span>
                              )}
                            </div>
                            {r.company_name && (
                              <p className="text-xs text-muted-foreground mt-0.5">{r.company_name}</p>
                            )}
                            {r.reply_subject && (
                              <p className="text-xs text-foreground mt-1 truncate" title={r.reply_subject}>
                                <span className="text-muted-foreground">Subject:</span> {r.reply_subject}
                              </p>
                            )}
                            {r.reply_preview && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2" title={r.reply_preview}>
                                {r.reply_preview}
                              </p>
                            )}
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {rearmDate && (
                                <>Auto-rearm on <span className="font-semibold text-foreground">{rearmDate.toLocaleDateString()}</span>{daysUntil != null && daysUntil > 0 ? ` (in ${daysUntil} days)` : daysUntil != null && daysUntil <= 0 ? " (eligible now)" : ""}</>
                              )}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRearmNow(r.customer_email)}
                            data-testid={`rearm-now-btn-${r.customer_email}`}
                          >
                            Re-arm now
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Database Stats */}
            {dbStats && (
              <div className="mb-6 p-4 bg-background rounded-lg border border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-primary" />
                  Database: {dbStats.database_name}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {Object.entries(dbStats.collections || {}).map(([name, count]) => (
                    <div key={name} className="bg-card p-3 rounded border border-border/50">
                      <p className="text-xs text-muted-foreground truncate" title={name}>
                        {name.replace(/_/g, ' ')}
                      </p>
                      <p className="text-lg font-bold text-foreground">{count}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-border flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Documents</span>
                  <span className="text-xl font-bold text-primary">{dbStats.total_documents}</span>
                </div>
              </div>
            )}

            {/* Export Section */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-success/5 rounded-lg border border-success/30">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Download className="h-5 w-5 text-success" />
                  Export Database
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Download a complete backup of all data as JSON. Use this before deployment or for regular backups.
                </p>
                <Button 
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-success hover:bg-success/90 text-white w-full"
                  data-testid="export-db-btn"
                >
                  {exporting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FileJson className="h-4 w-4 mr-2" />
                      Export All Data
                    </>
                  )}
                </Button>
              </div>

              {/* Import Section */}
              <div className="p-4 bg-warning/5 rounded-lg border border-warning/30">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Upload className="h-5 w-5 text-warning" />
                  Import Database
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Restore data from a previously exported JSON file. Use in production after deployment.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-foreground text-sm">Merge Mode</Label>
                    <Select value={mergeMode} onValueChange={setMergeMode}>
                      <SelectTrigger className="mt-1 bg-background border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">Skip existing (safe)</SelectItem>
                        <SelectItem value="replace">Replace existing (overwrite)</SelectItem>
                        <SelectItem value="merge">Merge (add new only)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <input
                      type="file"
                      accept=".json"
                      ref={fileInputRef}
                      onChange={(e) => setImportFile(e.target.files[0])}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-border hover:border-warning/50"
                      data-testid="select-import-file-btn"
                    >
                      <FileJson className="h-4 w-4 mr-2" />
                      {importFile ? importFile.name : "Select JSON File"}
                    </Button>
                  </div>
                  
                  <Button 
                    onClick={handleImport}
                    disabled={importing || !importFile}
                    className="bg-warning hover:bg-warning/90 text-black w-full"
                    data-testid="import-db-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Import Data
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Import Results */}
            {importResult && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-6 p-4 bg-background rounded-lg border border-border"
              >
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-success" />
                  Import Results
                </h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-success">{importResult.summary?.total_imported || 0}</p>
                    <p className="text-xs text-muted-foreground">Imported</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-muted-foreground">{importResult.summary?.total_skipped || 0}</p>
                    <p className="text-xs text-muted-foreground">Skipped</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-warning">{importResult.summary?.total_replaced || 0}</p>
                    <p className="text-xs text-muted-foreground">Replaced</p>
                  </div>
                </div>
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    View details by collection
                  </summary>
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {Object.entries(importResult.collections || {}).map(([name, data]) => (
                      <div key={name} className="flex justify-between py-1 border-b border-border/30">
                        <span className="text-foreground">{name}</span>
                        <span className="text-muted-foreground">
                          +{data.imported || 0} / ~{data.skipped || 0} / ↻{data.replaced || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              </motion.div>
            )}

            {/* Production Backup Note */}
            <div className="mt-6 p-3 bg-primary/5 rounded-lg border border-primary/20">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Production Note:</strong> For daily automated backups to your NAS servers, 
                configure a cron job to call the export API and save to your storage location. 
                Example: <code className="text-xs bg-background px-1 rounded">curl -H "Authorization: Bearer TOKEN" {API_BASE_URL}/api/admin/database/export &gt; /nas/backups/$(date +%Y%m%d).json</code>
              </p>
            </div>

            {/* CSV/Excel Data Import Section */}
            <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/30">
              <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                Data Import (CSV/Excel)
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Import Position Folders, Transport Orders, or Bill of Lading documents from CSV or Excel files.
                Choose between first-time import (replaces all) or update import (adds/updates).
              </p>
              <div className="flex flex-wrap gap-3">
                <DataImportButton 
                  token={token} 
                  dataType="position-folders" 
                  onSuccess={() => fetchDbStats()}
                  variant="outline"
                  size="default"
                />
                <DataImportButton 
                  token={token} 
                  dataType="transport-orders" 
                  onSuccess={() => fetchDbStats()}
                  variant="outline"
                  size="default"
                />
                <DataImportButton 
                  token={token} 
                  dataType="bl-documents" 
                  onSuccess={() => fetchDbStats()}
                  variant="outline"
                  size="default"
                />
              </div>
            </div>

            {/* Metrans Rates Import/Export Section */}
            <div className="mt-6 p-4 bg-orange-500/5 rounded-lg border border-orange-500/30">
              <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                <Train className="h-5 w-5 text-orange-500" />
                Metrans Rail Rates
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Export Metrans rail rates for backup or import them to restore/migrate.
                Export creates a JSON file, import supports both JSON and CSV.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const response = await axios.get(
                        `${API_BASE_URL}/api/pricing/metrans/export-rates`,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `metrans_rates_${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success(`Exported ${response.data.rates_count} Metrans rates`);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to export Metrans rates");
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Metrans
                </Button>
                <DataImportButton 
                  token={token} 
                  dataType="metrans-rates" 
                  onSuccess={() => fetchDbStats()}
                  variant="outline"
                  size="default"
                />
              </div>
            </div>

            {/* Route Pricing Configuration Section */}
            <div className="mt-6 p-4 bg-purple-500/5 rounded-lg border border-purple-500/30">
              <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-purple-500" />
                Route & LTL Pricing Configuration
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure the base rate, fuel surcharge, handling fees and margin for LTL/Consol shipment calculations.
                These settings affect the Route Calculator pricing estimates.
              </p>
              
              {pricingLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Transport Rate Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Truck className="h-4 w-4 text-green-500" />
                        FTL Rate (€/km)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={pricingConfig.ftl_base_rate_per_km}
                        onChange={(e) => setPricingConfig({...pricingConfig, ftl_base_rate_per_km: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                        placeholder="1.30-1.40"
                      />
                      <p className="text-xs text-muted-foreground">Full Truck Load rate (€1.30-1.40)</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Truck className="h-4 w-4 text-purple-500" />
                        LTL Rate (€/km)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={pricingConfig.ltl_base_rate_per_km}
                        onChange={(e) => setPricingConfig({...pricingConfig, ltl_base_rate_per_km: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                        placeholder="1.80-1.90"
                      />
                      <p className="text-xs text-muted-foreground">Part Load rate (€1.80-1.90)</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Fuel className="h-4 w-4 text-amber-500" />
                        Fuel Surcharge (%)
                      </Label>
                      <Input
                        type="number"
                        step="1"
                        value={pricingConfig.fuel_surcharge_percent}
                        onChange={(e) => setPricingConfig({...pricingConfig, fuel_surcharge_percent: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">Adjustable weekly</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Margin (%)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={pricingConfig.margin_percent}
                        onChange={(e) => setPricingConfig({...pricingConfig, margin_percent: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">Consolidation margin</p>
                    </div>
                  </div>
                  
                  {/* Additional Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-border/50">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Handling Fee (€)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={pricingConfig.handling_cost_per_shipment}
                        onChange={(e) => setPricingConfig({...pricingConfig, handling_cost_per_shipment: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">Per LTL shipment</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Min. Charge (€)</Label>
                      <Input
                        type="number"
                        step="10"
                        value={pricingConfig.min_charge_eur}
                        onChange={(e) => setPricingConfig({...pricingConfig, min_charge_eur: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">Minimum LTL price</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Diesel Price (€/L)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={pricingConfig.diesel_price_per_liter}
                        onChange={(e) => setPricingConfig({...pricingConfig, diesel_price_per_liter: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">Reference price</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">kg per LDM</Label>
                      <Input
                        type="number"
                        step="50"
                        value={pricingConfig.kg_per_ldm}
                        onChange={(e) => setPricingConfig({...pricingConfig, kg_per_ldm: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">Weight factor</p>
                    </div>
                  </div>
                  
                  {/* Additional Settings Row 2 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Max Truck LDM</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={pricingConfig.max_truck_ldm}
                        onChange={(e) => setPricingConfig({...pricingConfig, max_truck_ldm: parseFloat(e.target.value) || 0})}
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">Standard trailer</p>
                    </div>
                  </div>
                  
                  {/* Formula Preview */}
                  <div className="p-3 bg-purple-100/50 dark:bg-purple-900/20 rounded-lg text-sm">
                    <div className="font-medium text-purple-800 dark:text-purple-300 mb-2">Pricing Formula Preview:</div>
                    <div className="text-purple-700 dark:text-purple-400 space-y-1">
                      <div><strong>FTL:</strong> (Distance × €{pricingConfig.ftl_base_rate_per_km}/km) + {pricingConfig.fuel_surcharge_percent}% fuel surcharge</div>
                      <div><strong>LTL:</strong> (Distance × €{pricingConfig.ltl_base_rate_per_km}/km) + {pricingConfig.fuel_surcharge_percent}% fuel → ÷ {pricingConfig.max_truck_ldm} LDM × Cargo LDM + €{pricingConfig.handling_cost_per_shipment} + {pricingConfig.margin_percent}%</div>
                      <div className="text-xs text-purple-600 dark:text-purple-500">Min. charge: €{pricingConfig.min_charge_eur} | LTL uses higher rate (€{pricingConfig.ltl_base_rate_per_km}/km) to cover consolidation costs</div>
                    </div>
                  </div>
                  
                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button 
                      onClick={savePricingConfig}
                      disabled={pricingSaving}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {pricingSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Settings className="h-4 w-4 mr-2" />
                          Save Pricing Config
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* European Fuel Prices Section */}
            <div className="mt-6 p-4 bg-amber-500/5 rounded-lg border border-amber-500/30">
              <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                <Globe className="h-5 w-5 text-amber-500" />
                European Fuel Prices (AMZS)
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Live diesel prices from European countries. Click "Apply" to update the diesel price in your pricing configuration.
              </p>
              
              <div className="flex gap-2 mb-4">
                <Button
                  onClick={scrapeFuelPrices}
                  disabled={fuelScraping}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {fuelScraping ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Scraping...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Update Fuel Prices
                    </>
                  )}
                </Button>
                {fuelPrices?.scraped_at && (
                  <span className="text-xs text-muted-foreground self-center">
                    Last updated: {new Date(fuelPrices.scraped_at).toLocaleString()}
                  </span>
                )}
              </div>
              
              {fuelLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
                </div>
              ) : fuelPrices?.prices?.length > 0 ? (
                <div className="space-y-4">
                  {/* Key Countries Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {fuelPrices.prices
                      .filter(p => ["SI", "DE", "AT", "IT", "HR", "HU", "CZ", "SK", "PL", "FR", "NL", "BE"].includes(p.country_code))
                      .map((price) => (
                        <div 
                          key={price.country_code}
                          className={`p-3 rounded-lg border ${
                            price.country_code === "SI" 
                              ? "bg-amber-100 dark:bg-amber-900/30 border-amber-300" 
                              : "bg-background border-border"
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-medium text-sm">{price.country}</div>
                              <div className="text-xs text-muted-foreground">{price.country_code}</div>
                            </div>
                            {price.diesel_eur && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs hover:bg-amber-200"
                                onClick={() => applyFuelPrice(price.country_code)}
                                title={`Apply €${price.diesel_eur}/L to config`}
                              >
                                <Zap className="h-3 w-3 mr-1" />
                                Apply
                              </Button>
                            )}
                          </div>
                          <div className="space-y-1 text-xs">
                            {price.diesel_eur && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Diesel:</span>
                                <span className="font-semibold text-amber-700 dark:text-amber-400">
                                  €{price.diesel_eur.toFixed(3)}
                                </span>
                              </div>
                            )}
                            {price.petrol_95_eur && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Petrol 95:</span>
                                <span className="font-medium">€{price.petrol_95_eur.toFixed(3)}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {price.last_update}
                          </div>
                        </div>
                      ))}
                  </div>
                  
                  {/* Current vs Market Comparison */}
                  {(() => {
                    const sloveniaPrice = fuelPrices.prices.find(p => p.country_code === "SI")?.diesel_eur;
                    if (!sloveniaPrice) return null;
                    const diff = pricingConfig.diesel_price_per_liter - sloveniaPrice;
                    const diffPercent = ((diff / sloveniaPrice) * 100).toFixed(1);
                    return (
                      <div className={`p-3 rounded-lg ${
                        Math.abs(diff) > 0.05 
                          ? "bg-orange-100 dark:bg-orange-900/20 border border-orange-300" 
                          : "bg-green-100 dark:bg-green-900/20 border border-green-300"
                      }`}>
                        <div className="flex items-center gap-2 text-sm">
                          <TrendingUp className="h-4 w-4" />
                          <span>
                            Your config: <strong>€{pricingConfig.diesel_price_per_liter}</strong> | 
                            Slovenia market: <strong>€{sloveniaPrice.toFixed(3)}</strong>
                            {Math.abs(diff) > 0.01 && (
                              <span className={diff > 0 ? "text-red-600 ml-2" : "text-green-600 ml-2"}>
                                ({diff > 0 ? "+" : ""}{diff.toFixed(3)} / {diffPercent}%)
                              </span>
                            )}
                          </span>
                          {Math.abs(diff) > 0.05 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="ml-auto h-7"
                              onClick={() => applyFuelPrice("SI")}
                            >
                              Sync to market
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  <p className="text-xs text-muted-foreground">
                    Source: <a href={fuelPrices.source_url} target="_blank" rel="noopener noreferrer" className="underline">{fuelPrices.source}</a>
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Globe className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>No fuel prices available. Click "Update Fuel Prices" to fetch latest data.</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export { AdminPage };
